import { useEffect, useState } from "react";
import { REGION_CHANGE_EVENT, getRegion } from "../utils/region";

const LS_KEY = "pc_cookie_consent_v1";
const DEFAULTS = { essential: true, analytics: false, marketing: false, time: 0 };

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULTS);
  const [settings, setSettings] = useState(false);

  useEffect(() => {
    const boot = () => {
      if (!getRegion()) return;
      try {
        const saved = JSON.parse(localStorage.getItem(LS_KEY));
        if (!saved) setOpen(true);
        else {
          setPrefs(saved);
          applyConsent(saved);
        }
      } catch {
        setOpen(true);
      }
    };
    boot();
    window.addEventListener(REGION_CHANGE_EVENT, boot);
    return () => window.removeEventListener(REGION_CHANGE_EVENT, boot);
  }, []);

  const save = (next) => {
    const payload = { ...next, time: Date.now() };
    localStorage.setItem(LS_KEY, JSON.stringify(payload));
    applyConsent(payload);
  };

  const applyConsent = (c) => {
    const GA = import.meta.env.VITE_GA_ID;
    const existing = document.getElementById("ga-script");
    if (existing) existing.remove();
    if (c.analytics && GA) {
      const s = document.createElement("script");
      s.id = "ga-script";
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${GA}`;
      document.head.appendChild(s);

      const init = document.createElement("script");
      init.appendChild(
        document.createTextNode(`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date()); gtag('config', '${GA}', { anonymize_ip: true });
      `)
      );
      document.head.appendChild(init);
    }
  };

  const acceptAll = () => {
    const n = { essential: true, analytics: true, marketing: true };
    setPrefs(n);
    save(n);
    setOpen(false);
  };
  const rejectAll = () => {
    const n = { essential: true, analytics: false, marketing: false };
    setPrefs(n);
    save(n);
    setOpen(false);
  };
  const saveChoices = () => {
    save(prefs);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="pc-cookie" role="dialog" aria-labelledby="cookie-title">
      {!settings ? (
        <>
          <div className="pc-cookie-copy">
            <h3 id="cookie-title">Cookies on this site</h3>
            <p>
              Essential cookies keep the site working. Analytics and marketing
              stay off unless you allow them.
            </p>
          </div>
          <div className="pc-cookie-actions">
            <button type="button" onClick={rejectAll}>Reject extra</button>
            <button type="button" className="is-fill" onClick={acceptAll}>Accept all</button>
            <button type="button" className="is-text" onClick={() => setSettings(true)}>Settings</button>
          </div>
        </>
      ) : (
        <>
          <div className="pc-cookie-copy">
            <h3 id="cookie-title">Cookie settings</h3>
            <div className="pc-cookie-toggles">
              <Toggle label="Essential" desc="Required for core features." checked disabled />
              <Toggle
                label="Analytics"
                desc="Anonymous usage analytics."
                checked={prefs.analytics}
                onChange={(v) => setPrefs({ ...prefs, analytics: v })}
              />
              <Toggle
                label="Marketing"
                desc="Measurement and remarketing."
                checked={prefs.marketing}
                onChange={(v) => setPrefs({ ...prefs, marketing: v })}
              />
            </div>
            <p className="pc-cookie-legal">
              See our <a href="/legal/privacy">Privacy Policy</a> and <a href="/legal/cookies">Cookie Policy</a>.
            </p>
          </div>
          <div className="pc-cookie-actions">
            <button type="button" onClick={() => setSettings(false)}>Back</button>
            <button type="button" className="is-fill" onClick={saveChoices}>Save</button>
          </div>
        </>
      )}
    </div>
  );
}

function Toggle({ label, desc, checked = false, onChange = () => {}, disabled = false }) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? "opacity-70" : ""}`}>
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 accent-[#1c1915]"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
      />
      <div>
        <div className="text-sm font-medium text-[#1c1915]">{label}</div>
        <div className="text-xs text-[#3f3a34]">{desc}</div>
      </div>
    </label>
  );
}
